<template>
  <!-- Onglets de classeur : l'onglet jaune est le document affiché, il se prolonge dans la page juste en dessous -->
  <div class="document-tabs" role="tablist" aria-label="Documents du contrat">
    <template v-for="group in navGroups" :key="group.key">
      <button
          v-for="item in group.items"
          :key="item.key"
          type="button"
          role="tab"
          class="doc-tab"
          :aria-selected="item.isActive"
          :class="{ 'is-active': item.isActive, 'is-frozen': item.isAccounting, 'is-editable': !item.isAccounting }"
          :title="stateLabel(item)"
          @click="select(item.type, item.creditNoteNumber)"
      >
        <!-- font-awesome-icon et non <span class="fas ..."> : Font Awesome remplace les <span> par des <svg> (dom.watch), ce qui casse les v-if/v-else de Vue -->
        <font-awesome-icon class="doc-state" :icon="item.isAccounting ? 'lock' : 'pen'" />
        <span class="doc-label">{{ tabLabel(group) }} n°{{ item.number }}</span>
        <span v-if="item.tag" class="doc-tag">{{ item.tag }}</span>
        <span v-if="item.amount" class="doc-amount">{{ item.amount }}</span>
      </button>
    </template>
  </div>
</template>

<script>
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import {getDocumentTtcAmount, isAccountingDocument} from "@/utils";
import {library} from '@fortawesome/fontawesome-svg-core';
import {faLock} from "@fortawesome/free-solid-svg-icons";
import {faPen} from "@fortawesome/free-solid-svg-icons";

library.add(faLock);
library.add(faPen);

export default {
  name: 'DocumentsNavigation',
  props: {
    business: {type: Object, default: undefined},
    selectedDocument: {type: Object, required: true},
  },
  emits: ['update:selectedDocument'],
  computed: {
    navGroups() {
      if (!this.business) return [];
      const groups = [];

      if (this.business.number) {
        groups.push({
          key: 'quote',
          label: 'Devis',
          items: [{
            key: 'quote',
            number: this.business.number,
            tag: this.business.amendmentOfBusinessNumber ? 'Avenant' : null,
            type: BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value,
            creditNoteNumber: null,
            isActive: this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value,
          }],
        });
      }

      if (this.business.invoiceNumber) {
        groups.push({
          key: 'invoice',
          label: 'Facture',
          items: [{
            key: 'invoice',
            number: this.business.invoiceNumber,
            tag: null,
            type: BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value,
            creditNoteNumber: null,
            isActive: this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value,
          }],
        });
      }

      if (this.business.depositNumber) {
        groups.push({
          key: 'deposit',
          label: 'Acompte',
          items: [{
            key: 'deposit',
            number: this.business.depositNumber,
            tag: null,
            type: BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value,
            creditNoteNumber: null,
            isActive: this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value,
          }],
        });
      }

      const creditNotes = this.business.creditNotes || [];
      if (creditNotes.length) {
        groups.push({
          key: 'creditNotes',
          label: 'Avoirs',
          items: creditNotes.map(creditNote => ({
            key: `creditNote-${creditNote.number}`,
            number: creditNote.number,
            tag: null,
            type: BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value,
            creditNoteNumber: creditNote.number,
            isActive: this.selectedDocument.type === BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value
                && this.selectedDocument.creditNoteNumber === creditNote.number,
          })),
        });
      }

      // Valeur comptable du document : sert au montant affiché et au style "figé"
      groups.forEach(group => group.items.forEach(item => {
        item.isAccounting = isAccountingDocument(this.business, item);
        item.amount = this.formatAmount(getDocumentTtcAmount(this.business, item));
      }));

      return groups;
    },
  },
  methods: {
    // Libellé du type de document, au singulier (le groupe des avoirs est au pluriel)
    tabLabel(group) {
      return group.key === 'creditNotes' ? 'Avoir' : group.label;
    },
    stateLabel(item) {
      return item.isAccounting
          ? "Document comptabilisé : montant figé"
          : "Document en cours d'édition : montant provisoire";
    },
    formatAmount(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) return '';
      return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    },
    select(type, creditNoteNumber) {
      this.$emit('update:selectedDocument', {type, creditNoteNumber});
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

// Le liseré jaune en haut de la modale (BusinessManagementModal) et l'onglet actif ont la même couleur : ils ne font qu'un
.document-tabs
  display flex
  align-items flex-end
  gap 4px
  // Posés au-dessus du bord haut de la modale (le parent positionné est la modale) : le bas des onglets touche le liseré jaune
  position absolute
  bottom 100%
  left 24px
  right 56px
  // Pas de z-index : le contenu de la modale (CustomForm est isolé) doit passer au-dessus, y compris les modales ouvertes depuis celle-ci
  overflow-x auto
  overflow-y hidden

.doc-tab
  flex 0 0 auto
  display inline-flex
  align-items center
  gap 7px
  padding 9px 16px
  border 0
  border-radius 10px 10px 0 0
  background-color #cdd3db
  color #2b3340
  font-family inherit
  font-size 13.5px
  font-weight 600
  white-space nowrap
  cursor pointer
  transition background-color .15s ease

  &:hover
    background-color #bcc4cf

  .doc-state
    font-size 11px
    color #4a5160

  .doc-amount
    font-size 12px
    font-weight 600
    color #566070

  &.is-editable .doc-amount
    font-style italic

  .doc-tag
    padding 1px 6px
    border-radius 4px
    background-color THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE
    font-size 9.5px
    font-weight 700
    text-transform uppercase

  // Document affiché : jaune, plus haut que les autres, rattaché au liseré
  &.is-active
    padding-top 11px
    background-color PAGE_LEMON
    color #14181f
    font-weight 800

    .doc-state, .doc-amount
      color #14181f
</style>
